import { ArrowRight, Phone, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BLOG_IMAGE, BUSINESS } from "@/lib/siteData";

export const Blog = () => {
  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="py-20 sm:py-28 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="relative lg:sticky lg:top-28" data-testid="blog-nathan-photo">
            <div className="rounded-3xl overflow-hidden border border-green-100 shadow-xl shadow-green-900/10">
              <img
                src={BLOG_IMAGE}
                alt="Nathan — Pond & Garden Maintenance Services"
                loading="lazy"
                className="w-full h-full object-cover aspect-[4/3]"
              />
            </div>
            <div className="absolute -bottom-5 left-6 bg-green-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-lg">
              Nathan · East Grinstead
            </div>
          </div>

          <div>
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
              The Blog
            </span>
            <h2
              data-testid="blog-headline"
              className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-green-950 tracking-tight leading-[1.05]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Meet <span className="text-green-600">Nathan</span>.
            </h2>
            <p className="mt-3 text-lg text-slate-500 font-medium">
              The face behind Pond &amp; Garden.
            </p>
            <div className="mt-5 space-y-4 text-slate-600 text-base lg:text-lg leading-relaxed">
              <p>
                Hi, I&apos;m Nathan White, born and raised in East Grinstead. I&apos;ve always
                had a passion for the outdoors. Gardening has been in my family for
                generations, while ponds and lakes have become a passion of my own.
              </p>
              <p>
                That passion led me to achieve a First Class BSc (Hons) in Aquaculture and
                Fisheries Management, where a major part of my studies focused on pond and
                lake management, water quality, aquatic ecosystems, fish health, and
                habitat management. Alongside my degree, I spent five years working at
                Orchard Nursery Plant Centre Ltd, gaining valuable hands-on experience in
                gardening and plant care.
              </p>
              <p>
                Summer is one of the best times to maintain ponds and lakes. Regular
                cleaning, aquatic plant management, equipment checks, and maintaining good
                water quality all help create healthy environments for fish and wildlife
                while keeping them looking their best.
              </p>
              <p>
                With autumn just around the corner, now is also the perfect time to start
                planning an autumn clean. Preparing ponds and lakes before the leaves begin
                to fall helps protect water quality over winter and makes spring
                maintenance much easier.
              </p>
              <p>
                My aim is simple: to combine my knowledge, experience, and passion to help
                create outdoor spaces you can enjoy all year round. Whether you need pond or
                lake maintenance, seasonal cleans, or general garden care, get in touch
                today for a friendly chat (I cover properties within approximately
                30 minutes of my location).
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#contact" data-testid="blog-quote-cta-link">
                <Button
                  data-testid="blog-quote-cta"
                  className="bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-6 text-base font-semibold group"
                >
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href={`tel:${BUSINESS.phoneTel}`} data-testid="blog-call-cta-link">
                <Button
                  variant="outline"
                  data-testid="blog-call-cta"
                  className="rounded-full px-7 py-6 text-base font-semibold border-green-300 text-green-800 hover:bg-green-50"
                >
                  <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
                </Button>
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <span className="text-sm font-medium text-slate-500">Follow Nathan:</span>
              <a
                href={BUSINESS.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="blog-facebook-link"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-50 text-green-700 hover:bg-green-700 hover:text-white transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={BUSINESS.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="blog-instagram-link"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-50 text-green-700 hover:bg-green-700 hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Blog;
